import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_RULES } from '../constants/config';

// Raw dashboard "rules" as they arrive from the server: snake_case and untyped at
// this boundary. `any` here is deliberate — the reducer normalizes each field into
// the strongly-typed DashboardState below.
type DashboardRules = Record<string, any>;

interface DashboardState {
  customerId: number;
  dashboardCustomerName: string;
  name: string;
  email: string;
  createdAt: string | null;
  updatedAt: string | null;
  showHistoryChart: boolean;
  showPieChart: boolean;
  showFlsChart: boolean;
  showFlsCustomerChart: boolean;
  showFlsEmployeeChart: boolean;
  showOverviewIcon: boolean;
  showNpsChart: boolean;
  showENpsChart: boolean;
  showNpsDisChart: boolean;
  showFeedback: boolean;
  showEmpty: boolean;
  showImage: boolean;
  showCommentsBtn: boolean;
  showStarBtn: boolean;
  showHideBtn: boolean;
  showCloseBtn: boolean;
  showPublishBtn: boolean;
  showLikeBtn: boolean;
  enableAnalytics: boolean;
  layout: string | null;
  type: string | null;
  disabled: boolean;
  authenticated: boolean;
  rules: DashboardRules;
  isDashboardSaving: boolean;
  isDashboardValid: boolean;
  dashboardTargets: unknown[];
  selectedTargets: unknown[];
  error: boolean;
  errorMessage: string;
  emailCommentsEnabled: boolean;
  emailCommentsBcc: string;
  showPlsChart: boolean;
  timeFrom: string;
  timeTo: string;
  changeTarget: boolean;
  themeId: number;
  showDisabled: boolean;
  showStatus: boolean;
  notificationLink: boolean;
}

const initialState: DashboardState = {
  customerId: DEFAULT_RULES.customer_id,
  dashboardCustomerName: '',
  name: '',
  email: '',
  createdAt: null,
  updatedAt: null,
  showHistoryChart: DEFAULT_RULES.show_history_chart,
  showPieChart: DEFAULT_RULES.show_pie_chart,
  showFlsChart: DEFAULT_RULES.show_fls_chart,
  showFlsCustomerChart: DEFAULT_RULES.show_fls_customer_chart,
  showFlsEmployeeChart: DEFAULT_RULES.show_fls_employee_chart,
  showOverviewIcon: DEFAULT_RULES.show_overview_icon,
  showNpsChart: DEFAULT_RULES.show_nps_chart,
  showENpsChart: DEFAULT_RULES.show_enps_chart,
  showNpsDisChart: DEFAULT_RULES.show_nps_dis_chart,
  showFeedback: DEFAULT_RULES.show_feedback,
  showEmpty: DEFAULT_RULES.show_empty,
  showImage: DEFAULT_RULES.show_image,
  showCommentsBtn: DEFAULT_RULES.show_comments_btn,
  showStarBtn: DEFAULT_RULES.show_star_btn,
  showHideBtn: DEFAULT_RULES.show_hide_btn,
  showCloseBtn: DEFAULT_RULES.show_close_btn,
  showPublishBtn: DEFAULT_RULES.show_publish_btn,
  showLikeBtn: DEFAULT_RULES.show_like_btn,
  enableAnalytics: DEFAULT_RULES.enable_analytics,
  layout: DEFAULT_RULES.layout,
  // DEFAULT_RULES has no `type` key, so this was silently `undefined`. The load path
  // (requestDashboardRulesSuccess) normalizes a missing type to null, so seed null too.
  type: null,
  disabled: DEFAULT_RULES.disabled,
  authenticated: DEFAULT_RULES.authentication,
  rules: {},
  isDashboardSaving: false,
  isDashboardValid: false,
  dashboardTargets: [],
  selectedTargets: [],
  error: false,
  errorMessage: '',
  emailCommentsEnabled: DEFAULT_RULES.email_comments_enabled,
  emailCommentsBcc: DEFAULT_RULES.email_comments_bcc,
  showPlsChart: DEFAULT_RULES.show_pls_chart,
  timeFrom: '',
  timeTo: '',
  changeTarget: false,
  themeId: 1,
  showDisabled: true,
  showStatus: false,
  notificationLink: false,
};

// M6: RTK createSlice (largest slice). Reducers keep their plain-object bodies.
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    requestDashboardTargets(state) {
      return { ...state, error: false, errorMessage: '' };
    },
    requestDashboardTargetsError(state, action: PayloadAction<{ errorMessage: string }>) {
      return { ...state, error: true, errorMessage: action.payload.errorMessage };
    },
    requestDashboardTargetsSuccess(state, action: PayloadAction<unknown[]>) {
      return { ...state, error: false, errorMessage: '', dashboardTargets: action.payload };
    },
    requestDashboardRules(state) {
      return { ...state, error: false, errorMessage: '' };
    },
    requestDashboardRulesError(state, action: PayloadAction<{ errorMessage: string }>) {
      return { ...state, error: true, errorMessage: action.payload.errorMessage };
    },
    requestDashboardRulesSuccess(state, action: PayloadAction<DashboardRules>) {
      return {
        ...state,
        error: false,
        errorMessage: '',
        rules: action.payload || {},
        showHistoryChart: action.payload.show_history_chart || false,
        showPieChart: action.payload.show_pie_chart || false,
        showFlsChart: action.payload.show_fls_chart || false,
        showFlsCustomerChart: action.payload.show_fls_customer_chart || false,
        showFlsEmployeeChart: action.payload.show_fls_employee_chart || false,
        showOverviewIcon: action.payload.show_overview_icon || false,
        showNpsChart: action.payload.show_nps_chart || false,
        showENpsChart: action.payload.show_enps_chart || false,
        showNpsDisChart: action.payload.show_nps_dis_chart || false,
        showFeedback: action.payload.show_feedback || false,
        showEmpty: action.payload.show_empty || false,
        showImage: action.payload.show_image || false,
        showCommentsBtn: action.payload.show_comments_btn || false,
        showStarBtn: action.payload.show_star_btn || false,
        showHideBtn: action.payload.show_hide_btn || false,
        showCloseBtn: action.payload.show_close_btn || false,
        showPublishBtn: action.payload.show_publish_btn || false,
        showLikeBtn: action.payload.show_like_btn || false,
        enableAnalytics: action.payload.enable_analytics || false,
        layout: action.payload.layout || null,
        type: action.payload.type || null,
        disabled: action.payload.disabled || false,
        authenticated: action.payload.authentication || false,
        customerId: action.payload.customer_id || 0, // Zero is invalid
        emailCommentsEnabled: action.payload.email_comments_enabled || false,
        emailCommentsBcc: action.payload.email_comments_bcc || '',
        showPlsChart: action.payload.show_pls_chart || false,
        timeFrom: action.payload.time_from || '-infinity',
        timeTo: action.payload.time_to || 'infinity',
        changeTarget: action.payload.change_target || false,
        themeId: action.payload.theme_id || 1,
        showDisabled: action.payload.show_disabled,
        showStatus: action.payload.show_status,
        notificationLink: action.payload.notification_link,
      };
    },
    setSelectedTargets(state, action: PayloadAction<unknown[]>) {
      return { ...state, selectedTargets: action.payload };
    },
    // Writes an arbitrary form field by name; cast for the same reason as editUser.
    editDashboard(state, action: PayloadAction<{ property: string; value: unknown }>) {
      return { ...state, [action.payload.property]: action.payload.value } as DashboardState;
    },
    validateDashboard(state, action: PayloadAction<boolean>) {
      return { ...state, isDashboardValid: action.payload };
    },
    saveDashboard(state) {
      return { ...state, error: false, errorMessage: '', isDashboardSaving: true };
    },
    saveDashboardError(state, action: PayloadAction<{ errorMessage: string }>) {
      return { ...state, error: true, errorMessage: action.payload.errorMessage, isDashboardSaving: false };
    },
    saveDashboardSuccess(state) {
      return { ...state, error: false, errorMessage: '', isDashboardSaving: false };
    },
    requestDashboard(state) {
      return { ...state, error: false, errorMessage: '' };
    },
    requestDashboardError(state, action: PayloadAction<{ errorMessage: string }>) {
      return { ...state, error: true, errorMessage: action.payload.errorMessage };
    },
    requestDashboardSuccess(state, action: PayloadAction<DashboardRules>) {
      return {
        ...state,
        error: false,
        errorMessage: '',
        name: action.payload.name,
        email: action.payload.email,
        selectedTargets: action.payload.targets,
        createdAt: action.payload.created_at || action.payload.createdAt,
        updatedAt: action.payload.updated_at || action.payload.updatedAt
      };
    },
    requestDeleteDashboard(state) {
      return { ...state, isDashboardSaving: true, error: false, errorMessage: '' };
    },
    requestDeleteDashboardSuccess(state) {
      return { ...state, isDashboardSaving: false, error: false, errorMessage: '' };
    },
    requestDeleteDashboardError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isDashboardSaving: false, error: true, errorMessage: action.payload.errorMessage || '_delete_dashboard_error_' };
    },
    resetDashboard() {
      return initialState;
    },
  },
});

export const {
  requestDashboardTargets, requestDashboardTargetsError, requestDashboardTargetsSuccess,
  requestDashboardRules, requestDashboardRulesError, requestDashboardRulesSuccess,
  setSelectedTargets, editDashboard, validateDashboard,
  saveDashboard, saveDashboardError, saveDashboardSuccess,
  requestDashboard, requestDashboardError, requestDashboardSuccess,
  requestDeleteDashboard, requestDeleteDashboardSuccess, requestDeleteDashboardError,
  resetDashboard,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
